/**
 * Phase d'un trade dérivée de la date du trade et du statut du compte.
 * Aucune donnée de phase n'est stockée sur le trade — tout est calculé.
 */

export type TradePhase = "challenge" | "funded" | "live" | "demo";

interface TradeLike {
  date: Date;
}

interface AccountLike {
  status: string; // 'challenge' | 'pa' | 'inactive' | 'demo' | 'live'
  fundedAt: Date | null;
}

/**
 * Détermine la phase d'un trade.
 * - compte live  → 'live'
 * - compte demo  → 'demo'
 * - fundedAt défini ET date du trade >= fundedAt → 'funded'
 * - sinon → 'challenge'
 */
export function getTradePhase(trade: TradeLike, account: AccountLike): TradePhase {
  if (account.status === "live") return "live";
  if (account.status === "demo") return "demo";
  if (account.fundedAt && trade.date.getTime() >= account.fundedAt.getTime()) {
    return "funded";
  }
  return "challenge";
}

interface TradePnlLike {
  date: Date;
  pnl: number | null;
  commission: number | null;
  swaps: number | null;
}

interface AccountBalanceLike {
  status: string;
  fundedAt: Date | null;
  initialBalance: number;
  fundedStartingBalance: number | null;
  /** Date d'achat réelle (comptes historiques saisis sans trades). */
  purchasedAt?: Date | null;
  /**
   * Date du dernier reset prop firm. Une prop firm qui reset un challenge cramé
   * remet le solde à zéro en conservant le MÊME id broker : sans cette borne,
   * les pertes d'avant le reset continueraient d'amputer la balance affichée
   * alors que le broker repart de son solde de départ.
   */
  resetAt?: Date | null;
}

/** PnL net d'un trade (pnl + commission + swaps ; commission/swaps stockés négatifs). */
function netPnl(trade: TradePnlLike): number {
  return (trade.pnl ?? 0) + (trade.commission ?? 0) + (trade.swaps ?? 0);
}

/**
 * Dérive le solde de départ à partir d'un snapshot broker.
 *
 * Le snapshot broker (Tradovate `getcashbalancesnapshot`) rapporte le solde
 * COURANT, qui inclut déjà tout le PnL réalisé. Or l'app affiche le solde comme
 * `initialBalance + Σ netPnl(trades)`. Pour que ces deux mondes coïncident sans
 * compter le PnL deux fois, `initialBalance` doit être le solde AVANT les trades
 * importés : soldeBroker − Σ netPnl(trades). Le résultat est idempotent — le
 * réappliquer à chaque sync recale initialBalance pour maintenir l'invariant
 * `initialBalance + Σ netPnl == soldeBroker`.
 *
 * The broker snapshot is already reduced by withdrawals, and the app deducts
 * Payout rows again in currentBalance (accountEnrichment). Add recorded
 * payouts back so a confirmed payout is not double-counted.
 */
export function deriveInitialBalance(
  brokerBalance: number,
  trades: TradePnlLike[],
  payoutsTotal: number = 0,
): number {
  const sumNet = trades.reduce((sum, t) => sum + netPnl(t), 0);
  return brokerBalance + payoutsTotal - sumNet;
}

/**
 * Un trade est « archivé » quand il précède le dernier reset prop firm.
 *
 * Ces trades ne sont jamais supprimés : ils restent dans le journal et les
 * analytics (l'utilisateur garde son historique), ils sortent seulement du
 * calcul de balance. Exposée pour que l'UI puisse les badger comme archivés
 * sans réimplémenter la comparaison de bornes.
 */
export function isArchivedByReset(
  trade: TradeLike,
  account: { resetAt?: Date | null },
): boolean {
  if (!account.resetAt) return false;
  return trade.date.getTime() < account.resetAt.getTime();
}

/**
 * Balance d'un compte calculée par phase.
 * - compte funded ('pa') avec fundedAt : (fundedStartingBalance ?? initialBalance)
 *   + PnL des trades de phase 'funded' uniquement.
 * - sinon : initialBalance + PnL de tous les trades.
 * Un reset prop firm (`resetAt`) restreint en plus l'assiette aux trades postérieurs
 * au reset : les deux filtres se cumulent, un trade funded mais antérieur au reset
 * reste exclu.
 * Note : les payouts ne sont PAS déduits ici — le calcul payouts reste dans operations.ts.
 */
export function getAccountBalanceByPhase(
  account: AccountBalanceLike,
  trades: TradePnlLike[],
): number {
  // Borne de reset appliquée en amont : sans resetAt, `countable` est l'identité,
  // ce qui garantit la non-régression des comptes jamais resetés.
  const countable = trades.filter((t) => !isArchivedByReset(t, account));

  if (account.status === "pa" && account.fundedAt) {
    const startingBalance = account.fundedStartingBalance ?? account.initialBalance;
    const fundedPnl = countable
      .filter((t) => getTradePhase(t, account) === "funded")
      .reduce((sum, t) => sum + netPnl(t), 0);
    return startingBalance + fundedPnl;
  }
  const totalPnl = countable.reduce((sum, t) => sum + netPnl(t), 0);
  return account.initialBalance + totalPnl;
}

export interface ChallengeRecap {
  /** Nombre de trades de la phase challenge */
  tradeCount: number;
  /** Profit net cumulé de la phase challenge */
  netProfit: number;
  /** Balance atteinte en fin de challenge (initialBalance + netProfit) */
  finalBalance: number;
  /** Taux de réussite (0-100) des trades challenge */
  winRate: number;
  /** Durée du challenge en jours (premier trade challenge → fundedAt) */
  durationDays: number;
}

/**
 * Récap dynamique de la phase challenge d'un compte funded.
 * Retourne null si le compte n'est pas funded ('pa' avec fundedAt).
 */
export function computeChallengeRecap(
  account: AccountBalanceLike,
  trades: TradePnlLike[],
): ChallengeRecap | null {
  if (account.status !== "pa" || !account.fundedAt) return null;

  const challengeTrades = trades.filter(
    (t) => getTradePhase(t, account) === "challenge",
  );
  const tradeCount = challengeTrades.length;
  const netProfit = challengeTrades.reduce((sum, t) => sum + netPnl(t), 0);
  const winCount = challengeTrades.filter((t) => netPnl(t) > 0).length;
  const winRate = tradeCount > 0 ? (winCount / tradeCount) * 100 : 0;

  // Départ du challenge : le premier trade s'il existe, sinon la date d'achat
  // (comptes historiques reconstitués, qui n'ont aucun trade importé). On prend
  // la borne la plus ancienne des deux pour ne jamais raccourcir la durée.
  const candidateStarts: number[] = challengeTrades.map((t) => t.date.getTime());
  if (account.purchasedAt) candidateStarts.push(account.purchasedAt.getTime());

  let durationDays = 0;
  if (candidateStarts.length > 0) {
    const startTime = Math.min(...candidateStarts);
    const msPerDay = 1000 * 60 * 60 * 24;
    durationDays = Math.max(
      0,
      Math.round((account.fundedAt.getTime() - startTime) / msPerDay),
    );
  }

  return {
    tradeCount,
    netProfit,
    finalBalance: account.initialBalance + netProfit,
    winRate,
    durationDays,
  };
}
